/**
 * Signed in, on no brand, not an editor: a client whose account is not linked
 * to a brand yet. Two ways forward, in order of how often they happen: name a
 * brand and the portal is ready, or - when the company already works with
 * Aditor - get invited by whoever runs its portal.
 *
 * Replaces the editor gate's "No editor access" for clients (2026-10-08): a
 * client cannot act on a sentence about editors.
 *
 * @module web/screens/NoBrandScreen
 */
import { useEffect, useState } from "react";
import { ApiError, post, raw } from "../api/client.ts";

export function NoBrandScreen({ onCreated }: { onCreated: () => void }) {
  /** The signed-in address, from the server's no-brand answer. Null = unknown. */
  const [email, setEmail] = useState<string | null>(null);
  const [typedEmail, setTypedEmail] = useState("");
  const [brand, setBrand] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailed, setEmailed] = useState(false);

  useEffect(() => {
    raw<{ email?: string | null }>("/api/brand")
      .then(r => setEmail(r.body?.email ?? null))
      .catch(() => { /* the form still works with a typed address */ });
  }, []);

  const address = (email ?? typedEmail).trim();
  const canSubmit = brand.trim().length > 0 && address.length > 0 && !busy;

  async function submit() {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const res = await post<{ returning: boolean }>("/api/register", { name: brand.trim(), email: address });
      // A brand already uses this address: the link to it went to the inbox.
      if (res.returning) { setEmailed(true); setBusy(false); return; }
      // The server attached this account to the new brand; the cookie session
      // now resolves to it, so a refresh lands in onboarding.
      onCreated();
    } catch (e) {
      setError(e instanceof ApiError && e.status === 429
        ? "Too many tries. Wait a minute, then try again."
        : e instanceof Error ? e.message : "Something went wrong. Try again.");
      setBusy(false);
    }
  }

  async function signOut() {
    try { await fetch("/api/auth/logout", { method: "POST" }); } catch { /* best-effort */ }
    window.location.assign("/");
  }

  if (emailed) {
    return (
      <div className="screen-signup" data-screen="no-brand-emailed">
        <div className="ob-container">
          <h1 className="signup-title">Check your <span className="aurora-text">inbox</span></h1>
          <p className="signup-sub">Your brand is already set up with {address}. We sent you a sign-in link - open it and you are in.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="screen-signup" data-screen="no-brand">
      <div className="ob-container">
        <h1 className="signup-title">Set up your <span className="aurora-text">brand</span></h1>
        <p className="signup-sub">
          {email ? <>Signed in as <strong>{email}</strong>. </> : null}Name your brand and your portal is ready.
        </p>

        <form className="name-form" onSubmit={e => { e.preventDefault(); void submit(); }}>
          <div className="ob-input-step">
            {email === null && (
              <div className="field">
                <label htmlFor="no-brand-email">Your work email</label>
                <input id="no-brand-email" type="email" value={typedEmail} onChange={e => setTypedEmail(e.target.value)} />
              </div>
            )}
            <div className="field">
              <label htmlFor="no-brand-name">Brand name</label>
              <input id="no-brand-name" type="text" autoFocus value={brand} onChange={e => setBrand(e.target.value)} />
            </div>
            {error && <div className="signup-error">{error}</div>}
            <button type="submit" className="btn btn-primary" style={{ width: "100%" }} disabled={!canSubmit}>
              {busy ? "Setting up..." : "Create my portal"}
            </button>
          </div>
        </form>

        <div className="name-form" style={{ marginTop: 32 }}>
          <p className="t-body-sm" style={{ textAlign: "center", color: "var(--ink-2)" }}>
            <strong>Your company already works with Aditor?</strong><br />
            Ask whoever runs your portal to invite {email ? <strong>{email}</strong> : "your email"} under <strong>Team</strong>. The invite link opens your brand directly.
          </p>
          <p style={{ textAlign: "center", marginTop: 12 }}>
            <button type="button" className="btn btn-ghost" onClick={signOut}>Sign in with another account</button>
          </p>
        </div>
      </div>
    </div>
  );
}
